import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    AlertCircle,
    CheckCircle2,
    Loader2,
    MessageCircle,
    Store,
} from "lucide-react";
import { DEPOSIT, waLink } from "../../lib/site";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readPending = () => {
    try {
        return JSON.parse(sessionStorage.getItem("kore_pending_booking") || "null");
    } catch {
        return null;
    }
};

export default function PaymentResult({ type }) {
    const [state, setState] = useState(type === "success" ? "loading" : "cancelled");
    const [converted, setConverted] = useState(false);
    const pending = readPending();

    useEffect(() => {
        if (type !== "success") return;
        const sid = new URLSearchParams(window.location.search).get("session_id");
        if (!sid) {
            setState("error");
            return;
        }
        let attempts = 0;
        const timer = setInterval(async () => {
            attempts += 1;
            try {
                const res = await axios.get(`${API}/payments/status/${sid}`);
                if (res.data.payment_status === "paid") {
                    setState("paid");
                    clearInterval(timer);
                } else if (["failed", "expired"].includes(res.data.payment_status)) {
                    setState("failed");
                    clearInterval(timer);
                } else if (attempts >= 15) {
                    setState("timeout");
                    clearInterval(timer);
                }
            } catch {
                if (attempts >= 15) {
                    setState("error");
                    clearInterval(timer);
                }
            }
        }, 2000);
        return () => clearInterval(timer);
    }, [type]);

    const payInStudio = async () => {
        if (!pending?.booking_id) return;
        try {
            await axios.post(`${API}/bookings/${pending.booking_id}/pay-in-studio`);
            setConverted(true);
        } catch {
            setConverted(true);
        }
    };

    const waPaid = pending
        ? waLink(
              `Ciao Kore! Ho pagato online la caparra per: ${pending.class_name} — ${pending.day_label} ore ${pending.time}. Nome: ${pending.name}, Tel: ${pending.phone}.`,
          )
        : waLink("Ciao Kore! Ho appena pagato online la caparra per la mia lezione.");

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-linen px-6 py-16 text-ink">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md border border-hairline bg-white p-8 text-center"
            >
                <div className="mb-6 flex justify-center">
                    <LogoMark className="h-12 w-12" />
                </div>

                {state === "loading" && (
                    <div data-testid="payment-loading-view">
                        <Loader2 className="mx-auto h-10 w-10 animate-spin text-ink" strokeWidth={1.2} />
                        <h1 className="mt-4 text-2xl font-light tracking-tight">
                            Verifica del pagamento…
                        </h1>
                        <p className="mt-2 text-sm font-light text-body">
                            Un attimo, stiamo confermando la tua caparra.
                        </p>
                    </div>
                )}

                {state === "paid" && (
                    <div data-testid="payment-success-view">
                        <CheckCircle2 className="mx-auto h-12 w-12 text-ink" strokeWidth={1.2} />
                        <h1 className="mt-4 text-3xl font-light tracking-tight">
                            Caparra ricevuta!
                        </h1>
                        <p data-testid="payment-status-text" className="mt-2 text-sm font-light leading-relaxed text-body">
                            {pending
                                ? `${pending.class_name} · ${pending.day_label} ore ${pending.time}. `
                                : ""}
                            La tua caparra di {DEPOSIT.label} è confermata — il resto
                            lo paghi in studio.
                        </p>
                        <div className="mt-6 space-y-3">
                            <a
                                href={waPaid}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="payment-whatsapp-button"
                                className="flex w-full items-center justify-center gap-2 bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98]"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Invia conferma su WhatsApp
                            </a>
                            <a
                                href="/"
                                data-testid="payment-home-link"
                                className="block w-full border border-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-200 hover:bg-ink hover:text-white"
                            >
                                Torna alla home
                            </a>
                        </div>
                    </div>
                )}

                {(state === "failed" || state === "error" || state === "timeout") && (
                    <div data-testid="payment-failed-view">
                        <AlertCircle className="mx-auto h-12 w-12 text-ochre" strokeWidth={1.2} />
                        <h1 className="mt-4 text-3xl font-light tracking-tight">
                            {state === "timeout"
                                ? "Pagamento in elaborazione"
                                : "Pagamento non riuscito"}
                        </h1>
                        <p className="mt-2 text-sm font-light leading-relaxed text-body">
                            {state === "timeout"
                                ? "Stripe sta ancora elaborando: se hai completato il pagamento, la caparra risulterà registrata a breve."
                                : "Nessun addebito è stato effettuato. Puoi riprovare dalla pagina delle classi."}
                        </p>
                        <a
                            href="/#classi"
                            data-testid="payment-retry-link"
                            className="mt-6 block w-full bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-charcoal"
                        >
                            Torna alle classi
                        </a>
                    </div>
                )}

                {state === "cancelled" && (
                    <div data-testid="payment-cancel-view">
                        <AlertCircle className="mx-auto h-12 w-12 text-ochre" strokeWidth={1.2} />
                        <h1 className="mt-4 text-3xl font-light tracking-tight">
                            Pagamento annullato
                        </h1>
                        <p className="mt-2 text-sm font-light leading-relaxed text-body">
                            {converted
                                ? "Perfetto: il tuo posto resta riservato e pagherai tutto in studio."
                                : "Nessun addebito effettuato. Il tuo posto è ancora riservato: puoi decidere di pagare in studio."}
                        </p>
                        <div className="mt-6 space-y-3">
                            {!converted && pending?.booking_id && (
                                <button
                                    onClick={payInStudio}
                                    data-testid="payment-studio-button"
                                    className="flex w-full items-center justify-center gap-2 bg-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98]"
                                >
                                    <Store className="h-4 w-4" />
                                    Conferma e paga in studio
                                </button>
                            )}
                            <a
                                href="/"
                                data-testid="payment-home-link"
                                className="block w-full border border-ink py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-200 hover:bg-ink hover:text-white"
                            >
                                Torna alla home
                            </a>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
